import type { ReactNode } from 'react'
import { selectDeskIdentity, selectMainHeadingText, selectMainState, type SubsStoreState } from '@/msgdeskRuntime/state'
import { MainContent } from '@/deskUiSkeletons/Page/MainContent'

type MainStateLike = {
  subject?: string
  title?: string
  datetime?: string | number
  author?: string
  nameKey?: string
  codeKey?: string
  avatarPath?: string
  emailAddress?: string
  bodyHtml?: string
  body?: string
}

function cleanText(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined
  const text = String(value)
  return text.trim() ? text : undefined
}

function cleanDateText(value: unknown): string | undefined {
  if (value === undefined || value === null || value === 0 || value === '0') return undefined
  const text = String(value)
  return text.trim() ? text : undefined
}

export function readMainTemplateState(storeState: SubsStoreState): MainStateLike {
  return selectMainState(storeState)
}

export function mainMessageAvatarSrc(storeState: SubsStoreState, fallback: string) {
  const main = readMainTemplateState(storeState)
  return cleanText(main.avatarPath) || fallback
}

export function mainMessageEmailAddress(storeState: SubsStoreState, fallback: string) {
  const main = readMainTemplateState(storeState)
  if (cleanText(main.emailAddress)) return String(main.emailAddress)
  const codeKey = cleanText(main.codeKey)
  return codeKey ? `${codeKey}@msgdsk.com` : fallback
}

export function mainMessageEmailHref(storeState: SubsStoreState, fallback: string) {
  const address = mainMessageEmailAddress(storeState, '').trim()
  return address ? `mailto:${address}` : fallback
}

export function MainMessageTitleText({ storeState, fallback }: { storeState: SubsStoreState; fallback: string }) {
  const main = readMainTemplateState(storeState)
  return <>{cleanText(main.subject) || cleanText(main.title) || fallback}</>
}

export function MainMessageAuthorText({ storeState, fallback }: { storeState: SubsStoreState; fallback: string }) {
  const main = readMainTemplateState(storeState)
  return <>{cleanText(main.author) || cleanText(main.nameKey) || fallback}</>
}

// MAIN TITLE RULE: the main header shows "@ name_key" on a named-message desk
// and ":: subject" on an answer desk. When that answer subject is empty, the
// answer's codekey is its stable visible identity.
export function MainMessageHeadlineSign({ storeState }: { storeState: SubsStoreState }) {
  return <>{selectDeskIdentity(storeState).mainIsMsg ? '  @' : '  ::'}</>
}

export function MainMessageHeadlineText({ storeState, fallback }: { storeState: SubsStoreState; fallback: string }) {
  const main = readMainTemplateState(storeState)
  if (!selectDeskIdentity(storeState).mainIsMsg) {
    return <>{selectMainHeadingText(storeState) || fallback}</>
  }
  return <>{cleanText(main.nameKey) || cleanText(main.author) || fallback}</>
}

// LEGACY HEADLINE RULE (page.php:95): the separate small subject line hides
// when the desk is an answer OR the subject is empty — on answers the subject
// already IS the big title, so it must not render twice. The datetime beside
// it stays either way.
export function mainSubjectLineHidden(storeState: SubsStoreState): boolean {
  const main = readMainTemplateState(storeState)
  return !selectDeskIdentity(storeState).mainIsMsg || !cleanText(main.subject)
}

export function MainMessageDatetimeText({ storeState, fallback }: { storeState: SubsStoreState; fallback: string }) {
  const main = readMainTemplateState(storeState)
  return <>{cleanDateText(main.datetime) || fallback}</>
}

export function MainMessageBodyContent({
  storeState,
  children,
}: {
  storeState: SubsStoreState
  children: ReactNode
}) {
  void storeState
  void children
  return <MainContent />
}
